import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import FAQSchema from '../../../components/feature/FAQSchema';
import InternalLinks from '../../../components/feature/InternalLinks';

const YanginAlarmYonetmelikleri = () => {
  // React 19 native document title ve meta tag yönetimi
  useEffect(() => {
    document.title = 'Yangın Alarm Yönetmelikleri ve TSE Standartları | Güvenlik Rehberi | FiberNet Teknoloji';
    
    // Meta tags
    const updateMetaTag = (name: string, content: string, property?: string) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', 'Yangın alarm sistemi yönetmelikleri, TSE EN 54 standartları ve dedektör türleri hakkında detaylı rehber. Yasal gereksinimler ve uygulamalar.');
    updateMetaTag('keywords', 'yangın alarm yönetmelik, TSE EN 54, duman dedektör, ısı dedektör, yangın güvenlik, fabrika yangın alarm');
    updateMetaTag('author', 'FiberNet Teknoloji');
    updateMetaTag('robots', 'index, follow');
    
    // Open Graph tags
    updateMetaTag('og:title', 'Yangın Alarm Yönetmelikleri ve TSE Standartları | FiberNet Teknoloji', true);
    updateMetaTag('og:description', 'Yangın alarm sistemi yönetmelikleri, TSE EN 54 standartları ve dedektör türleri hakkında detaylı rehber.', true);
    updateMetaTag('og:image', 'https://public.readdy.ai/ai/img_res/640e2354df1ffc49645d5822dffbe968.jpg', true);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://fibernetteknoloji.com/blog/yangin-alarm-yonetmelikleri';
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: 'Yangın Alarm Yönetmelikleri' }
  ];

  const faqData = [
    {
      question: "Yangın alarm sistemi kurulumu zorunlu mu?",
      answer: "2017 tarihli Yangından Korunma Kanunu'na göre belirli alana sahip binalar, fabrikalar ve toplu kullanım alanları için yangın alarm sistemi kurulumu zorunludur. 2000 m² üzerindeki binalar için mutlaka gereklidir."
    },
    {
      question: "TSE standartları nedir ve neden önemli?",
      answer: "TSE EN 54 serisi yangın alarm sistemi standartlarıdır. Bu standartlar dedektör performansı, kontrol paneli özellikleri ve sistem entegrasyonu için minimum gereklilikleri belirler. Sigorta ve yasal uygunluk için zorunludur."
    },
    {
      question: "Hangi dedektör türü hangi alan için uygun?",
      answer: "Duman dedektörleri genel alanlarda, ısı dedektörleri mutfak ve kazan dairelerinde, alev dedektörleri ise yanıcı madde depolama alanlarında kullanılır. Alan özelliğine göre doğru seçim yapılmalıdır."
    },
    {
      question: "Yangın alarm sistemi bakımı ne sıklıkla yapılmalı?",
      answer: "Yönetmelik gereği yılda en az 2 kez profesyonel bakım yapılmalıdır. Aylık görsel kontroller ve haftalık test butonları kontrolü de gereklidir. Bakım kayıtları tutulmalıdır."
    }
  ];

  return (
    <>
      <Navbar />
      
      <main className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <section className="bg-gray-50 py-4">
          <div className="container mx-auto px-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-r from-red-600 to-red-700 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                Güvenlik Sistemleri
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Yangın Alarm Yönetmelikleri ve TSE Standartları
              </h1>
              <p className="text-xl text-red-100 mb-8">
                Yangın alarm sistemleri için yasal gereksinimler, TSE standartları ve dedektör türleri hakkında kapsamlı rehber
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-red-100">
                <span>📅 15 Ocak 2025</span>
                <span>⏱️ 7 dakika okuma</span>
                <span>🔥 Güvenlik Sistemleri</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Featured Image */}
              <div className="mb-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20fire%20alarm%20control%20panel%20interface%20with%20multiple%20zone%20indicators%2C%20smoke%20detectors%20heat%20sensors%20flame%20detectors%2C%20professional%20fire%20safety%20equipment%20in%20clean%20industrial%20building%20environment&width=800&height=450&seq=fire-alarm-panel&orientation=landscape"
                  alt="Yangın Alarm Sistemi Kontrol Paneli"
                  className="w-full h-96 object-cover rounded-lg shadow-lg"
                />
              </div>

              <div className="prose max-w-none">
                
                {/* Giriş */}
                <div className="mb-12">
                  <p className="text-lg text-gray-700 leading-8 mb-6">
                    Yangın alarm sistemleri, can ve mal güvenliği açısından kritik öneme sahip güvenlik sistemleridir. Türkiye'de yangın alarm sistemlerinin kurulumu, bakımı ve işletilmesi çeşitli yönetmelik ve standartlarla düzenlenmektedir. Bu rehberde, yasal gereksinimler ve TSE standartları hakkında detaylı bilgi bulabilirsiniz.
                  </p>
                </div>

                {/* Yasal Çerçeve */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Yangın Alarm Sistemleri Yasal Çerçevesi</h2>
                  
                  <div className="bg-red-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-red-800 mb-4">Temel Yasal Dayanaklar</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li><strong>Yangından Korunma Kanunu (2017):</strong> Yangın güvenlik sistemleri için temel yasal çerçeve</li>
                      <li><strong>Binaların Yangından Korunması Hakkında Yönetmelik:</strong> Bina türlerine göre yangın güvenlik gereksinimleri</li>
                      <li><strong>İş Sağlığı ve Güvenliği Kanunu:</strong> İşyerlerinde yangın güvenlik önlemleri</li>
                      <li><strong>TSE EN 54 Serisi Standartlar:</strong> Yangın algılama ve alarm sistemleri teknik standartları</li>
                    </ul>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white border-2 border-red-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🏢 Zorunlu Olan Binalar</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 2000 m² üzeri kapalı alan</li>
                        <li>• 4 kat ve üzeri binalar</li>
                        <li>• Toplu kullanım alanları</li>
                        <li>• Sanayi tesisleri</li>
                        <li>• Depo ve antrepolar</li>
                      </ul>
                    </div>
                    <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">⚡ Özel Gereksinimler</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Hastane ve sağlık tesisleri</li>
                        <li>• Okullar ve eğitim kurumları</li>
                        <li>• AVM ve ticaret merkezleri</li>
                        <li>• Oteller ve konaklama</li>
                        <li>• Petrol ve kimya tesisleri</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* TSE Standartları */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">TSE EN 54 Standartları</h2>
                  
                  <div className="mb-8">
                    <img 
                      src="https://readdy.ai/api/search-image?query=TSE%20fire%20safety%20certification%20documents%20with%20EN%2054%20standards%20compliance%20certificates%2C%20professional%20fire%20detection%20equipment%20testing%20laboratory%2C%20clean%20technical%20documentation%20background&width=800&height=400&seq=tse-standards&orientation=landscape"
                      alt="TSE Yangın Güvenlik Standartları"
                      className="w-full h-64 object-cover rounded-lg shadow-lg mb-6"
                    />
                  </div>

                  <div className="bg-orange-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-orange-800 mb-4">TSE EN 54 Serisi Ana Standartlar</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-orange-700 mb-3">Sistem Bileşenleri</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li><strong>EN 54-1:</strong> Genel gereksinimler</li>
                          <li><strong>EN 54-2:</strong> Kontrol ve gösterge panelleri</li>
                          <li><strong>EN 54-4:</strong> Güç kaynağı ekipmanları</li>
                          <li><strong>EN 54-13:</strong> Uyumluluk değerlendirmesi</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-700 mb-3">Algılama Ekipmanları</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li><strong>EN 54-5:</strong> Isı dedektörleri</li>
                          <li><strong>EN 54-7:</strong> Duman dedektörleri</li>
                          <li><strong>EN 54-10:</strong> Alev dedektörleri</li>
                          <li><strong>EN 54-11:</strong> Manuel çağrı butonu</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-8">
                    TSE EN 54 standartları Avrupa standardı olup, Türkiye'de yangın algılama ve alarm sistemleri için zorunlu teknik gereksinimleri belirler. Bu standartlara uygunluk, sistem güvenilirliği ve yasal uyum için kritik öneme sahiptir.
                  </p>
                </div>

                {/* Dedektör Türleri */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Yangın Dedektör Türleri ve Kullanım Alanları</h2>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white border-2 border-red-200 rounded-lg p-6">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3">
                          <i className="ri-fire-line text-2xl text-red-600"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Duman Dedektörleri</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-700 mb-4">
                        <li>• Optik (fotoelektrik) prensip</li>
                        <li>• İyonizasyon teknolojisi</li>
                        <li>• Erken yangın tespiti</li>
                        <li>• Genel kullanım alanları</li>
                      </ul>
                      <div className="text-xs text-gray-600">
                        <strong>Kullanım:</strong> Ofis, koridor, yatak odası, depo alanları
                      </div>
                    </div>

                    <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-3">
                          <i className="ri-temp-hot-line text-2xl text-orange-600"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Isı Dedektörleri</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-700 mb-4">
                        <li>• Sabit sıcaklık algılama</li>
                        <li>• Sıcaklık artış hızı</li>
                        <li>• Yüksek sıcaklık direnci</li>
                        <li>• Toz ve nem dayanıklılık</li>
                      </ul>
                      <div className="text-xs text-gray-600">
                        <strong>Kullanım:</strong> Mutfak, kazan dairesi, garaj, toz ortamlar
                      </div>
                    </div>

                    <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
                          <i className="ri-flashlight-line text-2xl text-blue-600"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Alev Dedektörleri</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-700 mb-4">
                        <li>• UV (ultraviyole) sensör</li>
                        <li>• IR (infrared) teknoloji</li>
                        <li>• Hızlı yangın tespiti</li>
                        <li>• Geniş açık alan kapsamı</li>
                      </ul>
                      <div className="text-xs text-gray-600">
                        <strong>Kullanım:</strong> Yanıcı madde depo, kimya fabrika, petrol tesis
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Dedektör Seçim Kriterleri</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-gray-700">
                        <li>✓ <strong>Alan Karakteristikleri:</strong> Tavan yüksekliği, havalandırma durumu</li>
                        <li>✓ <strong>Çevresel Koşullar:</strong> Nem, toz, sıcaklık değişimleri</li>
                        <li>✓ <strong>Yangın Türü:</strong> Beklenen yangın türü ve gelişim hızı</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>✓ <strong>Kullanım Amacı:</strong> Sürekli kullanım, geçici alan, depolama</li>
                        <li>✓ <strong>Bakım Gereksinimleri:</strong> Erişilebilirlik, temizlik sıklığı</li>
                        <li>✓ <strong>Maliyet Faktörleri:</strong> İlk yatırım ve işletme maliyetleri</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Kurulum ve Bakım */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Kurulum ve Bakım Gereksinimleri</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🔧 Kurulum Standartları</h3>
                      <div className="bg-teal-50 p-4 rounded-lg">
                        <ul className="space-y-3 text-gray-700">
                          <li><strong>Yetkili Firma:</strong> TSE belgeli kurulum firması gerekli</li>
                          <li><strong>Proje Onayı:</strong> İtfaiye müdürlüğü proje onayı</li>
                          <li><strong>Uygulama Projesi:</strong> Mimar/mühendis imzalı proje</li>
                          <li><strong>Malzeme Uygunluğu:</strong> CE işareti ve TSE belgesi</li>
                          <li><strong>Test ve Kabul:</strong> Sistem test raporu ve kabul belgesi</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🛠️ Bakım Programı</h3>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <ul className="space-y-3 text-gray-700">
                          <li><strong>Yıllık Bakım:</strong> Profesyonel teknik servis (2 kez/yıl)</li>
                          <li><strong>Aylık Kontrol:</strong> Görsel muayene ve genel durum</li>
                          <li><strong>Haftalık Test:</strong> Test butonları ve sesli uyarılar</li>
                          <li><strong>Dokümantasyon:</strong> Bakım kayıt defteri tutma</li>
                          <li><strong>Yedek Parça:</strong> Kritik bileşenler için yedek stok</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">⚠️ Önemli Uyarılar</h4>
                    <ul className="space-y-1 text-red-700">
                      <li>• Sistem devre dışı bırakılması yasal sorumluluk doğurur</li>
                      <li>• Yetkisiz müdahale ve değişiklik yasal olarak yasaktır</li>
                      <li>• Bakım kayıtları 5 yıl süreyle saklanmalıdır</li>
                      <li>• Sistem arızaları derhal yetkili servise bildirilmelidir</li>
                    </ul>
                  </div>
                </div>

                {/* İlgili Hizmetlerimiz */}
                <div className="mb-12">
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">İlgili Hizmetlerimiz</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <a 
                        href="/security-systems/fire-alarm-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-fire-line text-xl text-red-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-red-600">Yangın Alarm Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">TSE standartlarına uygun yangın algılama ve alarm sistemleri kurulumu ve bakım hizmetleri.</p>
                      </a>
                      
                      <a 
                        href="/services/building-fire-alarm-system" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-building-line text-xl text-orange-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-orange-600">Bina Yangın Alarm Sistemi</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Apartman, plaza ve ticari binalar için kapsamlı yangın alarm sistemi çözümleri.</p>
                      </a>

                      <a 
                        href="/security-systems/burglar-alarm-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-alarm-warning-line text-xl text-teal-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600">Hırsız Alarm Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Fabrika ve işyerleri için profesyonel hırsız alarm sistemi kurulumu.</p>
                      </a>

                      <a 
                        href="/security-systems/ip-camera-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-camera-line text-xl text-blue-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">IP Kamera Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Yüksek çözünürlüklü IP kamera sistemleri ile 7/24 güvenlik izleme çözümleri.</p>
                      </a>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Sıkça Sorulan Sorular</h2>
                  <FAQSchema faqs={faqData} />
                </div>

                {/* CTA Section */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-8 rounded-lg text-center">
                  <h2 className="text-2xl font-bold mb-4">TSE Standartlarına Uygun Yangın Alarm Sistemi</h2>
                  <p className="text-red-100 mb-6">
                    Fabrika ve işyeriniz için yönetmeliklere uygun yangın güvenlik çözümleri. 
                    Uzman ekibimizden ücretsiz keşif ve teklif alın.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="tel:+905537165561" 
                      className="bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-all duration-300 whitespace-nowrap"
                    >
                      📞 Hemen Ara: +90 553 716 55 61
                    </a>
                    <a 
                      href="/contact" 
                      className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all duration-300 whitespace-nowrap"
                    >
                      💬 Online Teklif Al
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <InternalLinks />
      </main>

      <Footer />
      <FloatingButtons />
    </>
  );
};

export default YanginAlarmYonetmelikleri;
